import { createFileRoute, Link } from '@tanstack/react-router';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { Badge, type BadgeVariant } from '~/components/ui/Badge';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import { PageTitle } from '~/components/ui/Page';
import {
  type NormalizedQuestion,
  QuestionAnswerView,
} from '~/components/ui/QuestionAnswerView';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';

type QuizResponseDetail = components['schemas']['QuizResponseDetail'];
type QuestionWithSubmission = components['schemas']['QuestionWithSubmission'];

export const Route = createFileRoute('/_admin/quiz-submissions/$submissionId')({
  component: QuizSubmissionDetail,
});

function statusBadge(detail: QuizResponseDetail): {
  label: string;
  variant: BadgeVariant;
} {
  if (!detail.complete) return { label: 'In progress', variant: 'neutral' };
  if (detail.passed) return { label: 'Passed', variant: 'success' };
  return { label: 'Failed', variant: 'error' };
}

function toNormalized(q: QuestionWithSubmission): NormalizedQuestion {
  return {
    question_id: q.question_id,
    type: q.type,
    status: q.status,
    text: q.text,
    choices: q.choices,
    user_answer: q.user_answer,
    has_answer: q.has_answer,
    is_correct: q.is_correct,
    feedback: null,
  };
}

function QuizSubmissionDetail() {
  const { submissionId } = Route.useParams();
  const { data, isLoading, error } = api.getQuizResponseDetail.useQuery({
    path: { response_id: Number(submissionId) },
  });

  if (isLoading) {
    return <LoadingMessage>Loading submission...</LoadingMessage>;
  }
  if (error) {
    return <ErrorMessage>Error loading submission</ErrorMessage>;
  }
  if (!data) {
    return <ErrorMessage>Submission not found</ErrorMessage>;
  }

  const { label, variant } = statusBadge(data);

  return (
    <Container>
      <BackLink to="/quiz-submissions">
        &larr; Back to Quiz Submissions
      </BackLink>

      <Header>
        <PageTitle>Quiz Submission #{data.id}</PageTitle>
        <HeaderActions>
          <Link
            to="/feedbacks"
            search={{ quiz_response_id: data.id }}
            style={{
              color: 'var(--blue)',
              textDecoration: 'none',
              fontSize: '0.875rem',
            }}
          >
            View grading feedback &rarr;
          </Link>
          <Badge $variant={variant} $size="md">
            {label}
          </Badge>
        </HeaderActions>
      </Header>

      <Section>
        <SectionTitle>Overview</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>Student</FieldLabel>
            <FieldValue>
              {data.user_display_name}
              <br />
              <small style={{ color: 'var(--grey-600)' }}>
                {data.user_email}
              </small>
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Course</FieldLabel>
            <FieldValue>{data.course_name}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Lesson</FieldLabel>
            <FieldValue>{data.lesson_name}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Score</FieldLabel>
            <FieldValue>{data.score}%</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Submitted</FieldLabel>
            <FieldValue>
              {new Date(data.created_at).toLocaleString()}
            </FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>Questions & Answers</SectionTitle>
        {data.questions.length === 0 ? (
          <EmptyMessage>
            No questions recorded for this submission.
          </EmptyMessage>
        ) : (
          data.questions.map((q, idx) => (
            <QuestionAnswerView
              key={q.question_id}
              question={toNormalized(q)}
              index={idx + 1}
            />
          ))
        )}
      </Section>
    </Container>
  );
}

const Container = styled.div``;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--blue);
  text-decoration: none;
  margin-bottom: 1rem;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const EmptyMessage = styled.div`
  padding: 1rem;
  color: var(--grey-600);
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--grey-600);
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--error);
`;
